import React, { useRef } from 'react';

export interface CornerScaleHandleProps {
  /** Current scale factor (1 = natural size). */
  scale: number;
  /** Live while dragging; the host stores the value. */
  onChange: (next: number) => void;
  /** The canvas the element has to fit inside; the scale is capped so the
      natural box × scale stays within its safe rectangle (`insets`, % of the
      canvas on each side; none = the whole canvas). */
  boundsRef?: React.RefObject<HTMLElement | null>;
  insets?: { top: number; bottom: number; left: number; right: number };
  min?: number;
  title?: string;
  onDraggingChange?: (dragging: boolean) => void;
}

const SIZE = 10;

/**
 * The bottom-right corner handle of a logo or category pill: drag away from
 * the element's top-left corner to grow it, toward it to shrink. One value
 * comes out (a scale factor), so every renderer keeps drawing the element
 * the way it does today, only bigger or smaller. No fixed ceiling: the
 * element may fill the canvas, never leave it.
 *
 * Rendered through ElementFrame's `handles` slot (so it exists only while
 * the element is selected) and tagged `data-overlay-handle`, which keeps
 * the stack drag from starting on it.
 */
const CornerScaleHandle: React.FC<CornerScaleHandleProps> = ({
  scale,
  onChange,
  boundsRef,
  insets,
  min = 0.2,
  title = 'Drag to resize',
  onDraggingChange,
}) => {
  const dragRef = useRef<{
    pointerId: number;
    originX: number;
    originY: number;
    startDist: number;
    startScale: number;
    max: number;
  } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget.parentElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const startScale = scale > 0 ? scale : 1;
    const startDist = Math.hypot(e.clientX - r.left, e.clientY - r.top) || 1;
    // The element at scale 1, so the cap follows the canvas however big it is drawn now.
    const naturalW = r.width / startScale;
    const naturalH = r.height / startScale;
    let max = Number.POSITIVE_INFINITY;
    const b = boundsRef?.current?.getBoundingClientRect();
    if (b && naturalW > 0 && naturalH > 0) {
      const safeW = b.width * (1 - ((insets?.left ?? 0) + (insets?.right ?? 0)) / 100);
      const safeH = b.height * (1 - ((insets?.top ?? 0) + (insets?.bottom ?? 0)) / 100);
      max = Math.max(min, Math.min(safeW / naturalW, safeH / naturalH));
    }
    dragRef.current = { pointerId: e.pointerId, originX: r.left, originY: r.top, startDist, startScale, max };
    onDraggingChange?.(true);

    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      const dist = Math.hypot(ev.clientX - d.originX, ev.clientY - d.originY);
      const next = Math.max(min, Math.min(d.max, d.startScale * (dist / d.startDist)));
      onChange(Math.round(next * 100) / 100);
    };
    const onUp = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d || ev.pointerId !== d.pointerId) return;
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      onDraggingChange?.(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      data-overlay-handle="corner"
      role="slider"
      aria-label={title}
      aria-valuenow={Math.round(scale * 100)}
      aria-valuetext={`${Math.round(scale * 100)} %`}
      title={title}
      onPointerDown={onPointerDown}
      style={{
        position: 'absolute',
        right: -(SIZE / 2) - 2,
        bottom: -(SIZE / 2) - 2,
        width: SIZE,
        height: SIZE,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,0,0,0.6)',
        boxShadow: '0 0 2px rgba(0,0,0,0.6)',
        cursor: 'nwse-resize',
        pointerEvents: 'auto',
        zIndex: 61,
      }}
    />
  );
};

export default CornerScaleHandle;
